import { useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { useAppointmentId } from '@eluve/frontend-appointment-hooks';
import { useNamedLogger } from '@eluve/logger';
import { useTenantIdFromParams } from '@eluve/session-helpers';

/**
 * Shared hook for regenerating appointment summaries.
 * Handles triggering the summarize endpoint.
 *
 * Components are responsible for:
 * - Determining when regeneration should be disabled (UI state)
 * - Success/error handling and user feedback (toasts, logs)
 * - Template selection (via BundleTemplateSelector, which persists directly)
 * - Analytics tracking
 */
export const useRegenerateSummary = () => {
  const logger = useNamedLogger('useRegenerateSummary');
  const tenantId = useTenantIdFromParams();
  const appointmentId = useAppointmentId();
  const apiClient = useApiClient();

  const [isRegenerating, setIsRegenerating] = useState(false);

  const [promptModelArgId, setPromptModelArgId] = useState<string | undefined>(
    undefined,
  );

  const regenerateSummary = async (
    onSuccess?: () => void,
    onError?: (error: Error) => void,
  ) => {
    setIsRegenerating(true);
    try {
      // Trigger summarization
      const result = await apiClient.llm.summarizeAppointment({
        body: {
          ...(promptModelArgId && { promptModelArgId }),
        },
        params: {
          tenantId: tenantId!,
          appointmentId,
        },
      });

      if (result.status !== 201) {
        throw new Error('Failed to start summarization job');
      }

      logger.info('Successfully triggered note regeneration', {
        appointmentId,
      });
      onSuccess?.();
    } catch (error) {
      logger.error('Failed to regenerate summary', {
        appointmentId,
        error,
      });
      onError?.(error as Error);
    } finally {
      setIsRegenerating(false);
    }
  };

  return {
    regenerateSummary,
    isRegenerating,
    promptModelArgId,
    setPromptModelArgId,
  };
};
